// src/components/TiKaneModal.jsx
import React, { useEffect, useMemo, useState } from 'react';
import { Modal, Button, Form, Row, Col, Alert } from 'react-bootstrap';
import { useForm, Controller } from 'react-hook-form';
import { AsyncPaginate } from 'react-select-async-paginate';
import clientService from '../services/clientService';

const PAGE_SIZE = 20;

// normalise (accents/casse)
const normalize = (str) => {
  if (str == null) return '';
  return String(str)
    .normalize('NFD')
    .replace(/\p{Diacritic}/gu, '')
    .toLowerCase()
    .replace(/\s+/g, ' ')
    .trim();
};

export default function TiKaneModal({
  show,
  onHide,
  onSave,
  initialData = null
}) {
  const init = initialData ?? {};
  const isEdit = !!init.id;
  const isMinorEdit = !!init.minorId; // ✅ compte mineur en édition

  const {
    control,
    register,
    handleSubmit,
    watch,
    reset,
    formState: { errors }
  } = useForm({
    defaultValues: {
      // ⚠️ IMPORTANT : si compte mineur, on ne pré-remplit PAS de clientId
      clientId:        isMinorEdit ? null : (init.clientId ?? null),
      startDate:       init.startDate ? String(init.startDate).slice(0,10) : '',
      durationDays:    init.durationDays ?? '',
      menCount:        init.menCount ?? '',
      status:          init.status ?? 'OPEN',
      feePercent:      init.feePercent ?? 5,
      feeFixedGourde:  init.feeFixedGourde ?? 0
    }
  });

  // calculs live
  const duration = parseFloat(watch('durationDays'))   || 0;
  const men      = parseFloat(watch('menCount'))       || 0;
  const feeP     = Math.max(0, Math.min(100, parseFloat(watch('feePercent')) || 0));
  const feeFix   = Math.max(0, parseFloat(watch('feeFixedGourde')) || 0);

  const daily    = men * 100;
  const total    = daily * duration;
  const percentFeeAmount = total * (feeP / 100);
  const finalRaw = total - percentFeeAmount - feeFix;
  const finalv   = Math.max(0, finalRaw);

  // pool de clients (pour le sélecteur côté adulte seulement)
  const [allClients, setAllClients] = useState([]);

  useEffect(() => {
    if (!show) return;

    reset({
      clientId:        isMinorEdit ? null : (init.clientId ?? null),
      startDate:       init.startDate ? String(init.startDate).slice(0,10) : '',
      durationDays:    init.durationDays ?? '',
      menCount:        init.menCount ?? '',
      status:          init.status ?? 'OPEN',
      feePercent:      init.feePercent ?? 5,
      feeFixedGourde:  init.feeFixedGourde ?? 0
    });

    // On ne charge la liste des clients QUE si on affiche le sélecteur
    if (!isMinorEdit) {
  clientService.list()
    .then((rows) => setAllClients(Array.isArray(rows) ? rows : []))
    .catch(() => setAllClients([]));
	} else {
	  setAllClients([]);
	}
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [show, init.id, init.clientId, init.startDate, init.durationDays, init.menCount, init.status, init.feePercent, init.feeFixedGourde, isMinorEdit, reset]);

  // Helpers options clients
  const toOption = (c) => ({
    value: c.id,
    label: `${c.firstName || ''} ${c.lastName || ''} — ${c.nifCin || 'NIF/CIN ?'}`.trim()
  });

const currentClientId = watch('clientId');

const selectedClientOption = useMemo(() => {
  if (isMinorEdit) return null;
  if (!currentClientId) return null;

  const found = allClients.find((c) => String(c.id) === String(currentClientId));
  return found
    ? toOption(found)
    : { value: currentClientId, label: `Client #${currentClientId}` };
}, [allClients, currentClientId, isMinorEdit]);

  const loadClientOptions = async (inputValue, loadedOptions, { page }) => {
    const q = normalize(inputValue);
    const filtered = allClients.filter(c => {
      const hay = normalize(
        `${c.firstName || ''} ${c.lastName || ''} ${c.nifCin || ''} ${c.email || ''} ${c.phone || ''}`
      );
      return hay.includes(q);
    });
    const start = (page - 1) * PAGE_SIZE;
    const slice = filtered.slice(start, start + PAGE_SIZE);
    const options = slice.map(toOption);
    const hasMore = start + PAGE_SIZE < filtered.length;

    return {
      options,
      hasMore,
      additional: { page: page + 1 }
    };
  };

  const submit = (vals) => {
    // Prépare le payload selon le type de compte
    const base = {
      startDate: vals.startDate,
      durationDays: vals.durationDays,
      menCount: vals.menCount,
      status: vals.status ?? init.status ?? 'OPEN',
      dailyValue: Number(daily.toFixed(2)),
      totalValue: Number(total.toFixed(2)),
      finalValue: Number(finalv.toFixed(2)),
      feePercent: Number(feeP.toFixed(2)),
      feeFixedGourde: Number(feeFix.toFixed(2))
    };

    let payload;

    if (isMinorEdit) {
      // 🧒 En mode mineur : on ne touche pas à l’identité
      payload = {
        ...base,
        id: init.id,
        accountNumber: init.accountNumber,
        // on conserve l’identité mineur côté backend (minorId/guardian…)
        // et on NE PASSE PAS de clientId
      };
    } else {
      // 👤 Adulte : clientId requis (création & édition)
      payload = {
        ...base,
        ...(init.id ? { id: init.id, accountNumber: init.accountNumber } : {}),
        clientId: vals.clientId ?? init.clientId ?? null
      };
    }

    onSave(payload);
  };

  // Infos lecture seule quand on édite un compte mineur
  const minorName = `${init?.Minor?.firstName || ''} ${init?.Minor?.lastName || ''}`.trim();
  const guardianName = `${init?.Guardian?.firstName || ''} ${init?.Guardian?.lastName || ''}`.trim();
  const guardianNif = init?.Guardian?.nifCin || '';

  return (
    <Modal show={show} onHide={onHide} backdrop="static" size="lg">
      <Modal.Header closeButton>
        <Modal.Title>
          {isEdit
            ? `Modifier un compte Ti Kanè${isMinorEdit ? ' (Mineur)' : ''}`
            : 'Ouvrir un compte Ti Kanè'}
        </Modal.Title>
      </Modal.Header>

      <Form onSubmit={handleSubmit(submit)}>
        <Modal.Body>
          {/* Bloc identité */}
          {!isMinorEdit ? (
            // === Mode ADULTE : sélecteur client visible ===
            <Form.Group className="mb-3">
              <Form.Label>Client (NIF/CIN affiché) *</Form.Label>
              <Controller
                control={control}
                name="clientId"
                rules={{ required: 'Veuillez choisir un client' }}
                render={({ field }) => (
                <AsyncPaginate
                    value={selectedClientOption}
                    onChange={(opt) => field.onChange(opt?.value ?? null)}
                    loadOptions={loadClientOptions}
                    additional={{ page: 1 }}
                    debounceTimeout={300}
                    defaultOptions={allClients.slice(0, PAGE_SIZE).map(toOption)}
                    placeholder="Rechercher par nom, NIF/CIN, téléphone…"
                    isClearable
                  />
                )}
              />
              {errors.clientId && (
                <Form.Text className="text-danger">{errors.clientId.message}</Form.Text>
              )}
            </Form.Group>
          ) : (
            // === Mode MINEUR : identité en lecture seule ===
            <Alert variant="secondary" className="mb-3">
              <div className="fw-bold mb-1">Compte mineur (identité non modifiable ici)</div>
              <div><strong>Mineur :</strong> {minorName || (init.minorId ? `#${init.minorId}` : '—')}</div>
              {/*<div><strong>Représentant :</strong> {guardianName || '—'} {guardianNif ? `(${guardianNif})` : ''}</div>*/}
            </Alert>
          )}

          {/* Date / Durée / Men / Frais */}
          <Row className="g-3">
            <Col md>
              <Form.Group controlId="startDate">
                <Form.Label>Date d’ouverture *</Form.Label>
                <Form.Control
                  type="date"
                  {...register('startDate', { required: 'Requis' })}
                />
                {errors.startDate && <Form.Text className="text-danger">{errors.startDate.message}</Form.Text>}
              </Form.Group>
            </Col>
            <Col md>
              <Form.Group controlId="durationDays">
                <Form.Label>Durée (jours) *</Form.Label>
                <Form.Control
                  type="number"
                  {...register('durationDays', { required: 'Requis', min: { value: 1, message: '>= 1' } })}
                />
                {errors.durationDays && <Form.Text className="text-danger">{errors.durationDays.message}</Form.Text>}
              </Form.Group>
            </Col>
            <Col md>
              <Form.Group controlId="menCount">
                <Form.Label>Nombre de men *</Form.Label>
                <Form.Control
                  type="number"
                  step="0.01"
                  {...register('menCount', { required: 'Requis', min: { value: 0, message: '>= 0' } })}
                />
                {errors.menCount && <Form.Text className="text-danger">{errors.menCount.message}</Form.Text>}
              </Form.Group>
            </Col>
            <Col md>
              <Form.Group controlId="feePercent">
                <Form.Label>Frais (%) *</Form.Label>
                <Form.Control
                  type="number"
                  step="0.01"
                  {...register('feePercent', {
                    required: 'Requis',
                    min: { value: 0, message: '>= 0' },
                    max: { value: 100, message: '<= 100' }
                  })}
                />
                {errors.feePercent && <Form.Text className="text-danger">{errors.feePercent.message}</Form.Text>}
              </Form.Group>
            </Col>
            <Col md>
              <Form.Group controlId="feeFixedGourde">
                <Form.Label>Frais fixe (HTG)</Form.Label>
                <Form.Control
                  type="number"
                  step="0.01"
                  {...register('feeFixedGourde', {
                    min: { value: 0, message: '>= 0' }
                  })}
                />
                {errors.feeFixedGourde && <Form.Text className="text-danger">{errors.feeFixedGourde.message}</Form.Text>}
              </Form.Group>
            </Col>
          </Row>

          {/* Calculs */}
          <Row className="mt-3">
            <Col><Form.Text>Valeur/jour : {daily.toFixed(2)}</Form.Text></Col>
            <Col><Form.Text>Total cumulé : {total.toFixed(2)}</Form.Text></Col>
            <Col><Form.Text>Frais % : {percentFeeAmount.toFixed(2)}</Form.Text></Col>
            <Col><Form.Text>Frais fixe : {feeFix.toFixed(2)}</Form.Text></Col>
            <Col><Form.Text>Valeur finale : {finalv.toFixed(2)}</Form.Text></Col>
          </Row>

          {/* En édition : n° de compte + statut */}
          {isEdit && (
            <>
              <Form.Group className="mt-3" controlId="accountNumber">
                <Form.Label>Numéro de compte</Form.Label>
                <Form.Control readOnly value={init.accountNumber || ''} />
              </Form.Group>
              <Form.Group className="mt-3" controlId="status">
                <Form.Label>Statut</Form.Label>
                <Form.Select {...register('status')}>
                  <option value="OPEN">Ouvert</option>
                  <option value="Payé">Payer le client et clôturer le compte</option>
                  <option value="EARLY_TERMINATED">Arrêt anticipé</option>
                </Form.Select>
              </Form.Group>
            </>
          )}
        </Modal.Body>

        <Modal.Footer>
          <Button variant="secondary" onClick={onHide}>Annuler</Button>
          <Button type="submit" variant="primary">
            {isEdit ? 'Mettre à jour' : 'Ouvrir le compte'}
          </Button>
        </Modal.Footer>
      </Form>
    </Modal>
  );
}
